import type { JSX } from "react";
import { Icon } from "../../components/ui";
import { cn } from "../../components/ui/cn";

/**
 * Pressable needs-reply bell attached to the Chats list-pane search
 * row. Pressed = the list is filtered to needs_input episodes and the
 * bell is highlighted with the existing orange accent token (same
 * `bg-accent` the active PillButton uses — nothing invented). It is
 * JUST a filter affordance: the numeric badge lives on the GLOBAL
 * command-bar NotificationBell only (round 8 — duplicating it here
 * was noise).
 */
// @tested-by: tst_fe_chats_bell_001..007
// @invariant: aria-pressed mirrors the filter state; accent background
// only when pressed; no numeric badge on this bell, ever
export function NeedsReplyBell({
  pressed,
  onToggle,
}: {
  readonly pressed: boolean;
  readonly onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      title="Needs reply"
      aria-label="Needs reply"
      aria-pressed={pressed}
      onClick={onToggle}
      // No mouse focus → no focus ring on click; Tab focus still rings.
      onMouseDown={(e) => { e.preventDefault(); }}
      className={cn(
        "relative w-9 h-9 shrink-0 rounded-lg flex items-center justify-center",
        "cursor-pointer border-none transition-colors duration-[120ms]",
        // IconButton focus pattern: outline only on keyboard focus,
        // never a ring on mouse click. (Tailwind v4: outline-none kills
        // outline-style, so focus-visible must restore `solid` too.)
        "outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-accent/70 focus-visible:outline-offset-2",
        pressed
          ? "bg-accent text-white"
          : "bg-transparent text-content-secondary hover:bg-surface-hover",
      )}
    >
      <Icon name="bell" size={16} />
    </button>
  );
}
